import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useToast } from '../context/ToastContext';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Logo from './Logo';

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handleRecovery = async () => {
            // Clean up the hash to handle double hashes like #recovery#access_token
            const fullHash = window.location.hash.replace(/#/g, '&');
            const params = new URLSearchParams(fullHash);

            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');

            if (accessToken && refreshToken) {
                try {
                    const { error } = await supabase.auth.setSession({
                        access_token: accessToken,
                        refresh_token: refreshToken
                    });
                    if (error) throw error;
                    setIsReady(true);
                } catch (err: any) {
                    console.error("SetSession Error:", err);
                    setError("Security error: " + err.message);
                }
            } else {
                // Retry once after 1s just in case Supabase is slow to mount
                const { data: { session } } = await supabase.auth.getSession();
                if (session) {
                    setIsReady(true);
                } else {
                    setTimeout(async () => {
                        const { data: { session: retrySession } } = await supabase.auth.getSession();
                        if (retrySession) setIsReady(true);
                        else setError("No security token found. Please request a new link.");
                    }, 1000);
                }
            }
        };

        handleRecovery();
    }, []);

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) throw error;

            showToast('Password updated successfully!', 'success');
            navigate('/login');
        } catch (err: any) {
            setError(err.message + ". Try refreshing if you just clicked the link.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full max-w-md animate-fade-in">
            <Link
                to="/"
                className="mb-8 flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-purple-600 transition-colors group"
            >
                <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" />
                Back to Home
            </Link>

            <div className="bg-white p-8 lg:p-10 rounded-[2.5rem] shadow-2xl border border-gray-100">
                <div className="text-center mb-10">
                    <div className="flex justify-center mb-6">
                        <Logo size="lg" showText={false} />
                    </div>
                    <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Set New Password</h2>
                    <p className="text-gray-500">Enter a secure password for your account.</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm font-bold text-center">
                        {error}
                    </div>
                )}

                <form onSubmit={handleReset} className="space-y-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700">New Password</label>
                        <input
                            required
                            type="password"
                            placeholder="••••••••"
                            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none transition-all"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700">Confirm Password</label>
                        <input
                            required
                            type="password"
                            placeholder="••••••••"
                            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none transition-all"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-5 bg-black text-white rounded-full text-lg font-bold hover:bg-purple-700 transition-all shadow-lg hover:shadow-purple-500/20 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                        {isLoading ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                        ) : 'Update Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
